import { baseApi } from './baseApi';

// Helper function to store access token.
// NOTE: the refresh token is intentionally NOT persisted in localStorage —
// the BE issues it as an HttpOnly+Secure cookie that the browser will send
// automatically on subsequent /auth/* requests (`credentials: 'include'`).
// Putting it in localStorage would expose it to any XSS payload.
const storeAccessToken = (response) => {
  if (response?.data?.token) {
    localStorage.setItem('token', response.data.token);
  }
  return response;
};

export const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Login
    login: builder.mutation({
      query: (credentials) => ({
        url: '/auth/login',
        method: 'POST',
        body: credentials,
        credentials: 'include', // Important for cookies
      }),
      transformResponse: (response) => {
        storeAccessToken(response);
        return response;
      },
      invalidatesTags: ['User'],
    }),
    
    // Logout
    logout: builder.mutation({
      query: () => ({
        url: '/auth/logout',
        method: 'POST',
        credentials: 'include', // Important for cookies
      }),
      transformResponse: (response) => {
        // Clear access token on logout. The refresh-token cookie is cleared
        // server-side by the /auth/logout endpoint. We still try to remove
        // any stale `refreshToken` key left over from older builds.
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        return response;
      },
      invalidatesTags: ['User'],
    }),

    // Get current user profile
    getProfile: builder.query({
      query: () => '/auth/me',
      providesTags: ['User'],
    }),

    // Update user profile
    updateProfile: builder.mutation({
      query: (profileData) => ({
        url: '/auth/profile',
        method: 'PATCH',
        body: profileData,
      }),
      invalidatesTags: ['User'],
    }),

    // Change password (Admin only on the BE)
    changePassword: builder.mutation({
      query: ({ currentPassword, newPassword }) => ({
        url: '/auth/change-password',
        method: 'POST',
        body: { currentPassword, newPassword },
      }),
    }),

    // One-time self-service password reset for students locked out by the
    // historical double-hash bug. The BE only honors this if the account
    // was already flagged eligible during a recent failed login attempt
    // AND the SELF_RESET_ENABLED env flag is true. See
    // controllers/auth.controller.js (toeic-server) for the full safety
    // contract. Treated like any other public endpoint here.
    selfResetPassword: builder.mutation({
      query: ({ username, firstName, lastName, newPassword }) => ({
        url: '/auth/self-reset-password',
        method: 'POST',
        body: { username, firstName, lastName, newPassword },
      }),
    }),
  }),
});

export const {
  useLoginMutation,
  useGetProfileQuery,
  useLazyGetProfileQuery,
  useUpdateProfileMutation,
  useChangePasswordMutation,
  useLogoutMutation,
  useSelfResetPasswordMutation,
} = authApi;
